/**
 * Tax-relevant computations: holding period classification and realized
 * gain/loss math for dispositions (sales and trades).
 *
 * Holding period rule of thumb (US): a position held > 365 days qualifies
 * for long-term capital gains treatment. Short-term gains are taxed as
 * ordinary income. Collectibles long-term gains are taxed up to 28% — but
 * the short-term vs long-term split still matters and is what we capture
 * here. Actual tax-rate selection is downstream of this module.
 *
 * Dates are expected as YYYY-MM-DD strings (the format used throughout the
 * app) but the helpers accept any ISO-compatible string. Returns
 * { days: null, treatment: "Unknown" } when either date is missing or
 * unparseable so the UI can surface a "fix your data" warning rather than
 * silently assume short-term.
 */

export type TaxTreatment = "Short-term" | "Long-term" | "Unknown";

export interface HoldingPeriodResult {
  /** Whole days between acquisition and disposition. Null when unknown. */
  days: number | null;
  treatment: TaxTreatment;
}

/** Parse a date string to a millisecond timestamp, or null on failure. */
function parseDateMs(d: any): number | null {
  if (!d) return null;
  if (typeof d === "number") return isFinite(d) ? d : null;
  const s = String(d).trim();
  if (!s) return null;
  const t = Date.parse(s);
  return isFinite(t) ? t : null;
}

/**
 * Holding period between an acquisition date and a disposition date.
 * Returns Unknown if either input is missing/unparseable. Days are computed
 * as floor((disposition - acquisition) / 86_400_000) so partial days don't
 * round up.
 *
 * Threshold: > 365 days = Long-term. (Strictly > 365 to match IRS guidance
 * — "more than one year".)
 */
export function holdingPeriod(acqDate: any, dispositionDate: any): HoldingPeriodResult {
  const acq = parseDateMs(acqDate);
  const disp = parseDateMs(dispositionDate);
  if (acq == null || disp == null) return { days: null, treatment: "Unknown" };
  const days = Math.floor((disp - acq) / 86_400_000);
  const treatment: TaxTreatment = days > 365 ? "Long-term" : "Short-term";
  return { days, treatment };
}

/** Format a HoldingPeriodResult for compact display (e.g. "412d · Long-term"). */
export function fmtHoldingPeriod(hp: HoldingPeriodResult): string {
  if (hp.days == null) return "Unknown";
  return `${hp.days}d · ${hp.treatment}`;
}

/** CSS color name for a tax treatment, matching the existing pill palette. */
export function taxTreatmentColor(t: TaxTreatment): "gray" | "blue" | "amber" {
  if (t === "Long-term") return "blue";
  if (t === "Short-term") return "amber";
  return "gray";
}
